import filePdfIconUrl from '@/assets/svg/FilePDF.svg?url';
import fileJpgIconUrl from '@/assets/svg/FileJPG.svg?url';
import fileJpegIconUrl from '@/assets/svg/FileJPEG.svg?url';
import filePngIconUrl from '@/assets/svg/FilePNG.svg?url';
import fileDocxIconUrl from '@/assets/svg/FileDOCX.svg?url';
import type {
  SubmittedFormDetails,
  SubmittedFormDetailsAttachment,
} from '@epics/taskFormManagement/types';
import { VIEW_SUBMITTED_FORM_LABELS as L } from '@epics/taskFormManagement/constants/labels';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FILE_ICONS: Record<string, string> = {
  JPG: fileJpgIconUrl,
  JPEG: fileJpegIconUrl,
  PNG: filePngIconUrl,
  PDF: filePdfIconUrl,
  DOC: fileDocxIconUrl,
  DOCX: fileDocxIconUrl,
  XLS: fileDocxIconUrl,
  XLSX: fileDocxIconUrl,
  CSV: fileDocxIconUrl,
  TXT: fileDocxIconUrl,
};

function getFileIcon(filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase() ?? '';
  return FILE_ICONS[ext] ?? filePdfIconUrl;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return L.EMPTY_VALUE;
  }
  const part = dateStr.split(/[ T]/)[0] ?? '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : dateStr;
}

function formatFieldValue(value: unknown, fieldType: string): string {
  if (value === null || value === undefined || value === '') {
    return L.EMPTY_VALUE;
  }
  switch (fieldType) {
    case 'date':
      return formatDate(String(value));
    case 'date_range': {
      const range = value as { start?: string; end?: string };
      const start = range.start ? formatDate(range.start) : L.EMPTY_VALUE;
      const end = range.end ? formatDate(range.end) : L.EMPTY_VALUE;
      return `${start} – ${end}`;
    }
    case 'checkbox': {
      const vals = Array.isArray(value) ? (value as string[]) : [];
      return vals.length > 0 ? vals.join(', ') : L.EMPTY_VALUE;
    }
    case 'attachment':
      return L.EMPTY_VALUE;
    default:
      return String(value);
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
}

function SectionCard({ title, children }: SectionCardProps) {
  return (
    <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
      <p className="text-[18px] font-medium text-primary-color-40">{title}</p>
      {children}
    </div>
  );
}

interface FileRowProps {
  filename: string;
  fileSize: number;
  url: string | undefined;
}

function FileRow({ filename, fileSize, url }: FileRowProps) {
  const inner = (
    <div className="flex items-center gap-3">
      <img
        src={getFileIcon(filename)}
        alt=""
        aria-hidden="true"
        className="w-10 h-10 shrink-0"
      />
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-neutral-10 truncate">
          {filename}
        </span>
        <span className="text-xs text-on-surface-variant">
          {formatFileSize(fileSize)}
        </span>
      </div>
    </div>
  );

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-70 transition-opacity"
      >
        {inner}
      </a>
    );
  }

  return <div>{inner}</div>;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  data: SubmittedFormDetails;
  clientSignatureUrl: string | null;
  techSignatureUrl: string | null;
  attachmentUrls: Record<string, string>;
}

export default function TaskReportTab({
  data,
  clientSignatureUrl,
  techSignatureUrl,
  attachmentUrls,
}: Props) {
  const { task_form } = data;
  const sections = [...(task_form.field_responses?.sections ?? [])].sort(
    (a, b) => a.order - b.order
  );

  const isImageMime = (mime: string) => mime.startsWith('image/');

  const clientSig = task_form.client_signature;
  const techSig = task_form.technician_signature;

  return (
    <div className="flex flex-col gap-3 m-[6px]">
      {/* Form name */}
      {task_form.form_name && (
        <p className="text-[18px] font-semibold text-on-surface-variant">
          {task_form.form_name}
        </p>
      )}

      <div className="flex flex-col gap-6">
        {/* Dynamic form sections — all field types rendered inline */}
        {sections.map((section) => {
          if (section.fields.length === 0) {
            return null;
          }
          return (
            <SectionCard key={section.id} title={section.name}>
              <div className="flex flex-col gap-6">
                {section.fields.map((field) => {
                  if (field.field_type === 'attachment') {
                    const files = Array.isArray(field.value)
                      ? (field.value as SubmittedFormDetailsAttachment[])
                      : [];
                    return (
                      <div
                        key={field.id}
                        className="flex min-w-0 flex-col gap-2"
                      >
                        <p className="text-bodySmall font-normal text-on-surface-variant">
                          {field.label}
                        </p>
                        <div className="flex flex-col gap-3">
                          {files.map((att) => (
                            <FileRow
                              key={att.s3_key}
                              filename={att.filename}
                              fileSize={att.size}
                              url={attachmentUrls[att.s3_key]}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={field.id} className="flex min-w-0 flex-col gap-2">
                      <p className="text-bodySmall font-normal text-on-surface-variant">
                        {field.label}
                      </p>
                      <p className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10">
                        {formatFieldValue(field.value, field.field_type)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          );
        })}

        {/* Client's Signature */}
        {clientSig && (
          <SectionCard title={L.SECTION_CLIENT_SIGNATURE}>
            {clientSignatureUrl && isImageMime(clientSig.fileType) ? (
              <div className="border border-[#DCDDE0] rounded-[8px] overflow-hidden bg-white flex items-center justify-center min-h-[160px] p-4">
                <img
                  src={clientSignatureUrl}
                  alt="Client signature"
                  className="max-w-full max-h-[200px] object-contain"
                />
              </div>
            ) : (
              <FileRow
                filename={clientSig.filename}
                fileSize={clientSig.fileSize}
                url={clientSignatureUrl ?? attachmentUrls[clientSig.path]}
              />
            )}
          </SectionCard>
        )}

        {/* Technician's Signature */}
        {techSig && (
          <SectionCard title={L.SECTION_TECH_SIGNATURE}>
            <FileRow
              filename={techSig.filename}
              fileSize={techSig.fileSize}
              url={techSignatureUrl ?? undefined}
            />
          </SectionCard>
        )}

        {/* Add Notes */}
        <SectionCard title={L.SECTION_NOTES}>
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-bodySmall font-normal text-on-surface-variant">
              {L.FIELD_TECH_NOTE}
            </p>
            <p className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10">
              {task_form.notes?.trim() ? task_form.notes : L.EMPTY_VALUE}
            </p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
